import { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { UploadCloud, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB — stays under proxy body limits
const ACCEPT = 'video/mp4,video/webm,video/quicktime,image/gif,image/png,image/jpeg,image/webp';
const MAX_BYTES = 200 * 1024 * 1024; // 200MB guard

// Drag-and-drop uploader → app object storage (chunked). Calls onUploaded(url) with /api/files/... path.
export function MediaUploader({ onUploaded, label = 'Upload video or image' }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error('File too large (max 200MB)'); return; }
    setDone(false); setProgress(0);
    const uploadId = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 40).padEnd(8, '0');
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    try {
      let resp;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(i));
        fd.append('total_chunks', String(total));
        fd.append('filename', file.name);
        fd.append('content_type', file.type || '');
        resp = await api.post('/exercise-media/upload-chunk', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const url = resp?.data?.url;
      if (url) { setDone(true); onUploaded?.(url); toast.success('Uploaded to storage'); }
      else toast.error('Upload finished but no URL returned');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setProgress(p => (p === 100 ? p : null));
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };

  return (
    <div
      data-testid="media-uploader"
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`cursor-pointer rounded-xl border-2 border-dashed p-4 text-center transition ${dragOver ? 'border-[#00C8FF] bg-[#00C8FF]/5' : 'border-[var(--c-hair)] hover:border-[var(--c-hair)]'}`}
    >
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid="media-uploader-input"
        onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
      {progress != null && progress < 100 ? (
        <div className="flex flex-col items-center gap-2 py-1">
          <Loader2 className="w-5 h-5 text-[#00C8FF] animate-spin" />
          <div className="text-xs text-muted-foreground">Uploading… {progress}%</div>
          <div className="w-full h-1.5 bg-[var(--c-film)] rounded-full overflow-hidden"><div className="h-full bg-[#00C8FF]" style={{ width: `${progress}%` }} /></div>
        </div>
      ) : done ? (
        <div className="flex items-center justify-center gap-2 py-1 text-[var(--c-green-ink)] text-sm font-semibold"><CheckCircle2 className="w-4 h-4" /> Uploaded</div>
      ) : (
        <div className="flex flex-col items-center gap-1.5 py-1">
          <UploadCloud className="w-5 h-5 text-[#00C8FF]" />
          <div className="text-sm font-medium">{label}</div>
          <div className="text-[11px] text-muted-foreground">Drag &amp; drop or click · MP4, WebM, MOV, GIF (max 200MB)</div>
        </div>
      )}
    </div>
  );
}
